/**
 * Kiosk device management (Phase 4 T06). Routes: /kiosks/devices,
 * /kiosks/devices/:deviceId
 *
 * Design doc §"Devices list/detail" + §"Register device / key handoff" +
 * §"Rotate / disable / revoke":
 * - The list/detail surface shows ONLY safe metadata returned by the service
 *   (keyPrefix, policyExpiresAt, lastSeenAt, status) — there is no plaintext
 *   retrieval path because none exists in the API.
 * - Register and rotate return the freshly minted key exactly once; it is
 *   held in component memory only. It is never written to localStorage /
 *   sessionStorage, never logged, and is dropped the moment the handoff panel
 *   closes (design bans web-storage credentials).
 * - Rotation calls the service's `POST /devices/:id/rotate-key`, whose
 *   documented lifecycle mints the replacement key/policies FIRST and revokes
 *   the old key second (auth-service edge-mint → edge-revoke). The UI never
 *   offers a "re-show key" affordance; if the operator missed it, the honest
 *   path is another rotation.
 */
import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';

import { EmptyState } from '@/components/shared/EmptyState';
import { LoadingRows } from '@/components/shared/LoadingRows';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StatusBadge } from '@/components/shared/StatusBadge';
import { formatKioskDate, kioskApiErrorText, KioskLoadError, KioskNavigation } from './KioskShared';
import {
  useDisableKioskDeviceMutation,
  useGetKioskDeviceQuery,
  useGetKioskDevicesQuery,
  useRegisterKioskDeviceMutation,
  useRevokeKioskDeviceMutation,
  useRotateKioskDeviceKeyMutation,
} from '@/store/api/kiosksApi';

export function KioskDevicesPage() {
  const { deviceId } = useParams<{ deviceId?: string }>();
  if (deviceId === 'new') return <KioskDeviceRegisterForm />;
  if (deviceId) return <KioskDeviceDetail deviceId={deviceId} />;
  return <KioskDevicesList />;
}

function KioskDevicesList() {
  const list = useGetKioskDevicesQuery({});
  return (
    <section className="space-y-4">
      <KioskNavigation active="devices" />
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold">Kiosk devices</h1>
          <p className="text-sm text-muted-foreground">
            Registered self-service devices. Keys are shown exactly once at registration or rotation and are never re-displayed.
          </p>
        </div>
        <Button asChild><Link to="/kiosks/devices/new">Register device</Link></Button>
      </header>

      {list.isLoading ? (
        <LoadingRows label="Loading kiosk devices" columns={5} />
      ) : list.isError ? (
        <KioskLoadError label="kiosk devices" retry={() => void list.refetch()} />
      ) : (list.data?.length ?? 0) === 0 ? (
        <EmptyState
          title="No kiosk devices registered yet."
          description="Register a device to mint its scoped key and assign a published template."
          action={<Button asChild><Link to="/kiosks/devices/new">Register device</Link></Button>}
        />
      ) : (
        <div role="region" tabIndex={0} className="overflow-x-auto rounded-md border" aria-label="Kiosk devices">
          <table className="w-full min-w-[56rem] text-sm">
            <thead className="bg-muted/50 text-left">
              <tr>
                <th scope="col" className="px-4 py-3">Device</th>
                <th scope="col" className="px-4 py-3">Status</th>
                <th scope="col" className="px-4 py-3">Scope</th>
                <th scope="col" className="px-4 py-3">Key prefix</th>
                <th scope="col" className="px-4 py-3">Policy expiry</th>
                <th scope="col" className="px-4 py-3">Last seen</th>
              </tr>
            </thead>
            <tbody>
              {list.data!.map((device) => (
                <tr key={device.id} className="border-t">
                  <td className="px-4 py-3">
                    <Link className="font-medium underline-offset-4 hover:underline" to={`/kiosks/devices/${encodeURIComponent(device.id)}`}>
                      {device.name}
                    </Link>
                    <span className="mt-1 block font-mono text-xs text-muted-foreground">{device.id}</span>
                  </td>
                  <td className="px-4 py-3"><StatusBadge status={device.status} /></td>
                  <td className="px-4 py-3">
                    <span className="block">Store: <span className="font-mono">{device.storeId}</span></span>
                    {device.workspaceId && <span className="block">Workspace: <span className="font-mono">{device.workspaceId}</span></span>}
                    {device.templateId && <span className="block">Template: <span className="font-mono">{device.templateId}</span></span>}
                  </td>
                  <td className="px-4 py-3 font-mono">{device.keyPrefix}…</td>
                  <td className="px-4 py-3">{formatKioskDate(device.policyExpiresAt)}</td>
                  <td className="px-4 py-3">{device.lastSeenAt ? formatKioskDate(device.lastSeenAt) : 'Never'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/**
 * One-time key handoff panel. The plaintext `oneTimeKey` prop exists ONLY for
 * the lifetime of this mounted component (parent state memory); closing the
 * handoff drops it. Nothing here touches web storage.
 */
function KeyHandoff({ oneTimeKey, deviceName, onDone }: {
  oneTimeKey: string;
  deviceName: string;
  onDone: () => void;
}) {
  const [copied, setCopied] = useState<'idle' | 'copied' | 'manual'>('idle');

  async function copyKey() {
    try {
      await navigator.clipboard?.writeText(oneTimeKey);
      setCopied('copied');
    } catch {
      setCopied('manual');
    }
  }

  return (
    <div role="region" aria-label="One-time device key" className="space-y-3 rounded-md border border-amber-500/40 bg-amber-500/10 p-4">
      <p className="text-sm font-semibold">Copy this key now. It won’t be shown again.</p>
      <p className="font-mono text-sm break-all" data-testid="one-time-key">{oneTimeKey}</p>
      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" onClick={() => void copyKey()}>Copy key</Button>
        {copied === 'copied' && <span role="status" className="text-sm text-muted-foreground">Key copied. Store it securely.</span>}
        {copied === 'manual' && <span role="status" className="text-sm text-muted-foreground">Clipboard unavailable — select and copy the key manually.</span>}
        <Button type="button" onClick={onDone}>Done</Button>
      </div>
      <p className="text-xs text-muted-foreground">
        This key is kept in memory only for the {deviceName} handoff and disappears when you press Done. It is never stored by this page.
      </p>
    </div>
  );
}

function KioskDeviceRegisterForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', storeId: '', workspaceId: '', templateId: '' });
  const [error, setError] = useState('');
  const [handoff, setHandoff] = useState<{ key: string; deviceId: string; name: string } | null>(null);
  const [registerDevice, registerState] = useRegisterKioskDeviceMutation();

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setError('');
    try {
      const result = await registerDevice({
        name: form.name.trim(),
        storeId: form.storeId.trim(),
        ...(form.workspaceId.trim() ? { workspaceId: form.workspaceId.trim() } : {}),
        ...(form.templateId.trim() ? { templateId: form.templateId.trim() } : {}),
      }).unwrap();
      setHandoff({ key: result.key, deviceId: result.device.id, name: result.device.name });
    } catch (caught) {
      setError(kioskApiErrorText(caught));
    }
  }

  if (handoff) {
    return (
      <section className="max-w-2xl space-y-4">
        <KioskNavigation active="devices" />
        <h1 className="text-2xl font-bold">Device registered</h1>
        <KeyHandoff
          key={handoff.key}
          deviceName={handoff.name}
          oneTimeKey={handoff.key}
          onDone={() => {
            const id = handoff.deviceId;
            setHandoff(null); // drops the plaintext from memory
            navigate(`/kiosks/devices/${encodeURIComponent(id)}`);
          }}
        />
      </section>
    );
  }

  return (
    <section className="space-y-4">
      <KioskNavigation active="devices" />
      <h1 className="text-2xl font-bold">Register device</h1>
      <p className="text-sm text-muted-foreground">
        The service mints a device-scoped key with an ephemeral policy through auth-service. The plaintext key is returned exactly once.
      </p>
      <form className="max-w-xl space-y-4" onSubmit={(event) => void submit(event)}>
        <div className="space-y-2">
          <Label htmlFor="device-name">Device name</Label>
          <Input id="device-name" required maxLength={100} placeholder="Front counter kiosk"
            value={form.name} onChange={(event) => setForm({ ...form, name: event.target.value })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="device-store">Store ID</Label>
          <Input id="device-store" required value={form.storeId}
            onChange={(event) => setForm({ ...form, storeId: event.target.value })}
            aria-describedby="device-store-help" />
          <span id="device-store-help" className="text-xs text-muted-foreground">Required. Use a returned store ID; the service validates scope.</span>
        </div>
        <div className="space-y-2">
          <Label htmlFor="device-workspace">Workspace ID (optional)</Label>
          <Input id="device-workspace" value={form.workspaceId} onChange={(event) => setForm({ ...form, workspaceId: event.target.value })} />
        </div>
        <div className="space-y-2">
          <Label htmlFor="device-template">Template ID (optional)</Label>
          <Input id="device-template" value={form.templateId} onChange={(event) => setForm({ ...form, templateId: event.target.value })}
            aria-describedby="device-template-help" />
          <span id="device-template-help" className="text-xs text-muted-foreground">Assign a kiosk template whose scope matches this store/workspace.</span>
        </div>
        {error && <p role="alert" className="text-sm text-destructive">{error}</p>}
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" disabled={registerState.isLoading} onClick={() => navigate('/kiosks/devices')}>Cancel</Button>
          <Button type="submit" disabled={!form.name.trim() || !form.storeId.trim() || registerState.isLoading}>
            {registerState.isLoading ? 'Registering…' : 'Register device'}
          </Button>
        </div>
      </form>
    </section>
  );
}

function KioskDeviceDetail({ deviceId }: { deviceId: string }) {
  const detail = useGetKioskDeviceQuery(deviceId);
  const [rotateKey, rotateState] = useRotateKioskDeviceKeyMutation();
  const [disableDevice, disableState] = useDisableKioskDeviceMutation();
  const [revokeDevice, revokeState] = useRevokeKioskDeviceMutation();
  const [handoff, setHandoff] = useState<{ key: string; kind: 'registered' | 'rotated' } | null>(null);
  const [confirmAction, setConfirmAction] = useState<'disable' | 'revoke' | null>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    // Rotation/disabling is destructive: drop the one-time key if this view unmounts.
    return () => setHandoff(null);
  }, []);

  const device = detail.data;

  async function rotate() {
    setError('');
    try {
      const result = await rotateKey(deviceId).unwrap();
      setHandoff({ key: result.key, kind: 'rotated' });
      await detail.refetch();
    } catch (caught) {
      setError(kioskApiErrorText(caught));
    }
  }

  async function runConfirm() {
    if (!confirmAction) return;
    setError('');
    try {
      if (confirmAction === 'disable') await disableDevice(deviceId).unwrap();
      else await revokeDevice(deviceId).unwrap();
      setConfirmAction(null);
      await detail.refetch();
    } catch (caught) {
      setError(kioskApiErrorText(caught));
    }
  }

  if (detail.isLoading) return <LoadingRows label="Loading kiosk device" columns={2} />;
  if (detail.isError || !device) return <KioskLoadError label="kiosk device" retry={() => void detail.refetch()} />;

  const busy = rotateState.isLoading || disableState.isLoading || revokeState.isLoading;

  return (
    <section className="space-y-5">
      <KioskNavigation active="devices" />
      <Button asChild variant="ghost"><Link to="/kiosks/devices">← Kiosk devices</Link></Button>
      <header>
        <h1 className="text-2xl font-bold">{device.name}</h1>
        <p className="text-sm text-muted-foreground">Device ID: <span className="font-mono">{device.id}</span> · Key prefix: <span className="font-mono">{device.keyPrefix}…</span></p>
      </header>

      {handoff ? (
        <KeyHandoff
          key={handoff.key}
          deviceName={device.name}
          oneTimeKey={handoff.key}
          onDone={() => setHandoff(null)}
        />
      ) : (
        <dl className="grid gap-4 border-y py-4 sm:grid-cols-2">
          <Detail label="Status" value={<StatusBadge status={device.status} />} />
          <Detail label="Store" value={device.storeId} />
          <Detail label="Workspace" value={device.workspaceId ?? 'Not assigned'} />
          <Detail label="Template" value={device.templateId ?? 'Not assigned'} />
          <Detail label="API key ID" value={device.apiKeyId} />
          <Detail label="Policy expires" value={formatKioskDate(device.policyExpiresAt)} />
          <Detail label="Last seen" value={device.lastSeenAt ? formatKioskDate(device.lastSeenAt) : 'Never'} />
          {device.revokedAt && <Detail label="Revoked at" value={formatKioskDate(device.revokedAt)} />}
        </dl>
      )}

      {error && <p role="alert" className="text-sm text-destructive">{error}</p>}

      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" disabled={busy || device.status === 'revoked'} onClick={() => void rotate()}>
          {rotateState.isLoading ? 'Rotating…' : 'Rotate key'}
        </Button>
        <Button type="button" variant="destructive" disabled={busy || device.status !== 'active'} onClick={() => setConfirmAction('disable')}>
          Disable device
        </Button>
        <Button type="button" variant="destructive" disabled={busy || device.status === 'revoked'} onClick={() => setConfirmAction('revoke')}>
          Revoke device
        </Button>
      </div>
      {device.status === 'revoked' && (
        <p className="text-sm text-muted-foreground">Revoked devices cannot be re-enabled or rotated — register a new device instead.</p>
      )}

      {confirmAction && (
        <div role="group" aria-label={`Confirm device ${confirmAction}`} className="space-y-3 rounded-md border border-destructive/40 p-4">
          <p>
            {confirmAction === 'disable'
              ? <>Disable <strong>{device.name}</strong>? The device stops serving immediately; the key stays revoked. This can be re-enabled later by re-registering a credential through rotation.</>
              : <>Revoke <strong>{device.name}</strong>? This is terminal — the key is revoked at auth-service and the device cannot be re-enabled or rotated.</>}
          </p>
          <div className="flex gap-2">
            <Button type="button" variant="destructive" disabled={busy} onClick={() => void runConfirm()}>
              {confirmAction === 'disable' ? 'Confirm disable' : 'Confirm revoke'}
            </Button>
            <Button type="button" variant="outline" onClick={() => setConfirmAction(null)}>Cancel</Button>
          </div>
        </div>
      )}
    </section>
  );
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium text-muted-foreground">{label}</dt>
      <dd className="mt-1 break-all text-sm">{value || '—'}</dd>
    </div>
  );
}
